import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="privacy-policy-page">
      <nav className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="FutureXV" className="h-10" />
              <span className="text-zinc-600">|</span>
              <span className="font-heading font-bold">Privacy Policy</span>
            </div>
            <Button onClick={() => navigate(-1)} variant="ghost" className="text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-primary-neon" />
          <h1 className="text-4xl font-heading font-bold" data-testid="privacy-heading">Privacy Policy</h1>
        </div>
        <div className="prose prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">1. Introduction</h2>
            <p className="text-zinc-300 leading-relaxed">FutureXV (Pty) Ltd ("FutureXV", "we", "us", or "our") is committed to protecting the personal information of all users of our platform. This Privacy Policy explains how we collect, use, store, and protect your personal information in compliance with the Protection of Personal Information Act 4 of 2013 (POPIA) and other applicable South African legislation.</p>
          </section>
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">2. Information We Collect</h2>
            <p className="text-zinc-300 leading-relaxed mb-3">We collect the following categories of personal information:</p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2">
              <li><strong>Identity Information:</strong> Full name, gender, age/date of birth</li>
              <li><strong>Contact Information:</strong> Email address, phone number</li>
              <li><strong>Player Information:</strong> Physical stats (height, weight), playing position, school/club affiliation, province, town</li>
              <li><strong>Media:</strong> Video links submitted by players</li>
              <li><strong>Verification Data:</strong> Coach endorsements, school/club confirmations</li>
              <li><strong>Account Data:</strong> Login credentials (password stored in hashed form only)</li>
              <li><strong>Parental Information:</strong> Parent/guardian email (for under-18 players)</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-zinc-300 space-y-2">
              <li>To create and manage your platform profile</li>
              <li>To enable scouts, coaches, and institutions to discover talent</li>
              <li>To verify player identities and credentials</li>
              <li>To facilitate communication between platform users</li>
              <li>To improve our services and user experience</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">4. Your Rights Under POPIA</h2>
            <p className="text-zinc-300 leading-relaxed mb-3">As a data subject, you have the right to:</p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Object:</strong> Object to the processing of your information</li>
              <li><strong>Withdrawal:</strong> Withdraw consent at any time</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
            </ul>
          </section>
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">5. Children's Privacy</h2>
            <p className="text-zinc-300 leading-relaxed">For users under 18, we require verified parental or guardian consent before activating any profile. Parent/guardian email must be provided and consent must be explicitly given through our platform. Parents retain full control over their child's profile visibility.</p>
          </section>
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">6. Data Security</h2>
            <p className="text-zinc-300 leading-relaxed">We implement industry-standard security measures including encrypted password storage (bcrypt hashing), secure HTTPS connections, role-based access control, and regular security audits. No profile data is made public without explicit consent and verification.</p>
          </section>
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">7. Data Retention</h2>
            <p className="text-zinc-300 leading-relaxed">We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy. You may request deletion of your account and all associated data at any time by contacting us.</p>
          </section>
          <section>
            <h2 className="text-2xl font-heading font-bold text-white mb-4">8. Contact Information</h2>
            <p className="text-zinc-300 leading-relaxed">For privacy-related queries, contact our Information Officer:</p>
            <p className="text-zinc-300">Email: info@futurexv.co.za</p>
            <p className="text-zinc-300">Phone: 082 637 3535</p>
          </section>
          <p className="text-zinc-500 text-sm mt-8">Last updated: February 2026</p>
        </div>
      </div>
    </div>
  );
}
